/**
 * StatusPill — minimal colored status indicator (smaller sibling of StateBadge).
 *
 * Phase 8 Prompt 3 — per audit deliverable 04D Section B universal status
 * color system. Where StateBadge (in components/help) renders an icon + label
 * + tooltip, StatusPill is the compact form-factor used in dense list rows
 * and inside the StatusHeader composite.
 *
 * Same color/icon registry as StateBadge — both pull from help-content.json
 * stateBadges section, so visual changes are content-edits not code-changes.
 *
 * Usage:
 *   <StatusPill status="active" />
 *   <StatusPill status="paused" size="medium" showLabel={false} />
 */

import React from "react";
import { Box, Tooltip } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ErrorIcon from "@mui/icons-material/Error";
import ArchiveIcon from "@mui/icons-material/Archive";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SendIcon from "@mui/icons-material/Send";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import { useHelpContent } from "../../contexts/HelpContentContext";

const ICON_MAP: Record<string, React.ComponentType<{ sx?: object }>> = {
  play_arrow: PlayArrowIcon,
  pause: PauseIcon,
  error: ErrorIcon,
  archive: ArchiveIcon,
  check_circle: CheckCircleIcon,
  schedule: ScheduleIcon,
  send: SendIcon,
  circle: CircleOutlinedIcon,
  logout: LogoutIcon,
};

export interface StatusPillProps {
  /** Status key — matches keys in help-content.json stateBadges. */
  status: string;
  /** Visual size. Default "small". */
  size?: "small" | "medium" | "large";
  /** Show the label text alongside the icon. Default true. */
  showLabel?: boolean;
  /** Show the icon. Default true. */
  showIcon?: boolean;
}

const SIZE_SPEC = {
  small: { padX: 0.7, padY: 0.2, font: 0.7, icon: 14, gap: 0.3 },
  medium: { padX: 1, padY: 0.35, font: 0.8, icon: 16, gap: 0.4 },
  large: { padX: 1.4, padY: 0.5, font: 0.95, icon: 20, gap: 0.5 },
};

export const StatusPill: React.FC<StatusPillProps> = ({
  status,
  size = "small",
  showLabel = true,
  showIcon = true,
}) => {
  const { getStateBadge } = useHelpContent();
  const entry = getStateBadge(status);
  const spec = SIZE_SPEC[size];

  if (!entry) {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          px: spec.padX,
          py: spec.padY,
          borderRadius: "999px",
          bgcolor: "grey.300",
          color: "text.primary",
          fontSize: `${spec.font}rem`,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {status}
      </Box>
    );
  }

  const IconComponent = entry.icon ? ICON_MAP[entry.icon] : null;
  const isDarkBg = entry.color !== "#D4AC0D"; // Warm Gold uses dark text
  const fgColor = isDarkBg ? "#FFFFFF" : "#1B1F23";

  const tooltipTitle = (
    <Box sx={{ p: 0.5 }}>
      <strong>{entry.label}</strong>
      <br />
      {entry.semantic}
    </Box>
  );

  return (
    <Tooltip title={tooltipTitle} placement="top" arrow enterDelay={400}>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: spec.gap,
          px: spec.padX,
          py: spec.padY,
          borderRadius: "999px",
          bgcolor: entry.color,
          color: fgColor,
          fontFamily: '"Montserrat", sans-serif',
          fontSize: `${spec.font}rem`,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          lineHeight: 1,
          whiteSpace: "nowrap",
          verticalAlign: "middle",
        }}
        aria-label={`Status: ${entry.label}. ${entry.semantic}`}
      >
        {showIcon && IconComponent && (
          <IconComponent sx={{ fontSize: spec.icon }} />
        )}
        {showLabel && entry.label}
      </Box>
    </Tooltip>
  );
};

export default StatusPill;
